/**
 * LiveTelemetryPanel - SSE-powered real-time telemetry viewer
 *
 * Modeled on LiveIngestPanel: SSE connection, raw vs decoded side-by-side,
 * pause/resume, stats bar.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Activity, Pause, Play, Trash2 } from 'lucide-react';
import { CP_BASE } from '../../lib/cpClient';
import { buildEventSourcePath } from '@/lib/eventSourceAuth';

interface TelemetryEvent {
  id: string;
  kind: string;
  device_id: string;
  raw_payload: any;
  iku_value: any;
  time: string;
  gateway_id?: string;
  template_id?: string;
  decoder_id?: string;
  entity_type?: string;
}

interface LiveTelemetryPanelProps {
  activeDeviceId?: string | null;
}

export default function LiveTelemetryPanel({ activeDeviceId }: LiveTelemetryPanelProps) {
  const [events, setEvents] = useState<TelemetryEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const [paused, setPaused] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [eventsPerSec, setEventsPerSec] = useState(0);

  const eventSourceRef = useRef<EventSource | null>(null);
  const pauseBufferRef = useRef<TelemetryEvent[]>([]);
  const eventsCountRef = useRef(0);
  const lastCountResetRef = useRef(Date.now());
  const eventIdCounter = useRef(0);

  const connectSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    const sseUrl = buildEventSourcePath(`${CP_BASE}/iot/telemetry/stream`, {
      ...(activeDeviceId ? { device_id: activeDeviceId } : {}),
    });
    if (!sseUrl) {
      setConnected(false);
      return;
    }

    const es = new EventSource(sseUrl);

    es.onopen = () => setConnected(true);

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.kind === 'data' || data.device_id) {
          const evt: TelemetryEvent = {
            id: `evt_${eventIdCounter.current++}`,
            kind: data.kind || 'data',
            device_id: data.device_id,
            raw_payload: data.raw_payload,
            iku_value: data.iku_value,
            time: data.time || new Date().toISOString(),
            gateway_id: data.gateway_id,
            template_id: data.template_id,
            decoder_id: data.decoder_id,
            entity_type: data.entity_type,
          };

          eventsCountRef.current += 1;

          if (paused) {
            pauseBufferRef.current.push(evt);
          } else {
            setEvents((prev) => [evt, ...prev].slice(0, 200));
          }
        }
      } catch { /* ignore parse errors */ }
    };

    es.addEventListener('connected', () => setConnected(true));
    es.addEventListener('heartbeat', () => { /* keep alive */ });

    es.onerror = () => {
      setConnected(false);
      // Auto-reconnect after 3 seconds
      setTimeout(() => {
        if (eventSourceRef.current === es) connectSSE();
      }, 3000);
    };

    eventSourceRef.current = es;
  }, [activeDeviceId, paused]);

  useEffect(() => {
    connectSSE();
    return () => {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };
  }, [connectSSE]);

  // Events/sec counter
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = (now - lastCountResetRef.current) / 1000;
      if (elapsed >= 1) {
        setEventsPerSec(Math.round(eventsCountRef.current / elapsed));
        eventsCountRef.current = 0;
        lastCountResetRef.current = now;
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  function handlePauseToggle() {
    if (paused) {
      // Flush buffer
      setEvents((prev) => [...pauseBufferRef.current.reverse(), ...prev].slice(0, 200));
      pauseBufferRef.current = [];
    }
    setPaused(!paused);
  }

  function handleClear() {
    setEvents([]);
    pauseBufferRef.current = [];
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50">
        <div className="flex items-center gap-2">
          <Activity className={`w-4 h-4 ${connected ? 'text-green-500' : 'text-gray-400'}`} />
          <span className="text-sm font-semibold text-gray-900">Live Telemetry</span>
          <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePauseToggle}
            className="p-1.5 rounded hover:bg-white/60 transition-colors"
            title={paused ? 'Resume' : 'Pause'}
          >
            {paused ? <Play className="w-4 h-4 text-green-600" /> : <Pause className="w-4 h-4 text-gray-500" />}
          </button>
          <button
            onClick={handleClear}
            className="p-1.5 rounded hover:bg-white/60 transition-colors"
            title="Clear"
          >
            <Trash2 className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex items-center gap-4 px-4 py-2 border-b border-gray-100 bg-gray-50 text-xs text-gray-500">
        <span>Events: <strong className="text-gray-700">{events.length}</strong></span>
        <span>Rate: <strong className="text-gray-700">{eventsPerSec}/s</strong></span>
        {activeDeviceId && (
          <span>Device: <strong className="text-gray-700">{activeDeviceId}</strong></span>
        )}
        {paused && (
          <span className="text-amber-600 font-medium">Paused ({pauseBufferRef.current.length} buffered)</span>
        )}
      </div>

      {/* Events list */}
      <div className="flex-1 overflow-y-auto">
        {events.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
            <Activity className="w-10 h-10 mb-3 opacity-30" />
            <p className="text-sm">Waiting for telemetry data...</p>
            <p className="text-xs mt-1">Events will appear here as devices send data</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {events.map((evt) => {
              const isExpanded = expandedId === evt.id;
              return (
                <div
                  key={evt.id}
                  className="px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : evt.id)}
                >
                  {/* Summary */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      <span className="text-xs font-medium text-gray-900">{evt.device_id}</span>
                      {evt.entity_type && (
                        <span className="text-xs text-gray-400">{evt.entity_type}</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400">
                      {new Date(evt.time).toLocaleTimeString()}
                    </span>
                  </div>

                  {/* Expanded: Raw vs IKU side by side */}
                  {isExpanded && (
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1">Raw Payload</div>
                        <pre className="text-xs p-2 bg-gray-100 rounded overflow-x-auto max-h-40">
                          {JSON.stringify(evt.raw_payload, null, 2)}
                        </pre>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-green-600 mb-1">Decoded (IKU)</div>
                        <pre className="text-xs p-2 bg-green-50 rounded overflow-x-auto max-h-40">
                          {evt.iku_value
                            ? JSON.stringify(evt.iku_value, null, 2)
                            : '(no decoded data)'}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
